// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Close from "@mcmc/XDL/Icons/Close";
import { layoutContainer } from "@mcmc/XDL/Utilities/sharedStyles";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    message = "message",
    selectPages = "selectPages",
    links = "links",
}

interface HeaderAlertBannerProps extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.selectPages]: boolean;
        [fields.links]: LinkModel[];
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentPageId: getCurrentPage(state).nodeId,
        bannersInCurrentPage: getWidgetsByPageId(state, getHeader(state).id).filter((widget: WidgetProps) => {
            return widget.type === "Header/HeaderAlertBanner";
        }),
    };
};

type Props = HeaderAlertBannerProps & ReturnType<typeof mapStateToProps>;

export interface HeaderAlertBannerStyles {
    container?: InjectableCss;
    bannerContent?: InjectableCss;
    close?: LinkPresentationProps;
}

export const headerAlertBannerStyles: HeaderAlertBannerStyles = {
    container: {
        css: css`
            background: ${getColor("primary.main")};
            color: ${getColor("primary.contrast")};
            border-bottom: 1px solid ${getColor("primary.contrast")};
            &:last-child {
                border: none;
            }
        `,
    },
    bannerContent: {
        css: css`
            ${layoutContainer};
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            min-height: 40px;
            padding: 10px;
            font-size: 16px;
            line-height: 19px;
            p {
                margin: 0;
                font-size: inherit;
                line-height: inherit;
            }
            ${StyledClickable} {
                ${StyledTypography} {
                    color: ${getColor("primary.contrast")};
                }
                &:hover {
                    ${StyledTypography} {
                        color: ${getColor("secondary")};
                    }
                }
            }
        `,
    },
    close: {
        color: "primary.contrast",
        hoverStyle: { color: "secondary" },
        icon: {
            iconProps: {
                src: Close as any,
                size: 16,
                css: css`
                    margin: 0;
                `,
            },
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            margin-left: 5px;
        `,
    },
};

const styles = headerAlertBannerStyles;

const shouldRenderThisBanner = (bannersInCurrentPage: WidgetProps[], links: LinkModel[], currentPageId: string) => {
    // the header has multiple banner widgets
    const hasMutlipleBanners = bannersInCurrentPage.length > 1;
    // the current rendering banner contains the current page in its links
    const thisBannerContainsCurrentPage = links.some(link => link.fields.destination.value === currentPageId);
    // the current banner is a general banner
    const thisBannerIsGeneral = !links.length;

    // the header has only one banner and its include this page in its links
    if (!hasMutlipleBanners && thisBannerContainsCurrentPage) {
        return true;
    }

    // the header has multiple banners and at least one of them includes this page in its links
    const containsMatchedSpecificBanner = bannersInCurrentPage.some(
        b =>
            b.fields.selectPages &&
            b.fields.links.some((bannerLink: LinkModel) => bannerLink.fields.destination.value === currentPageId),
    );

    // one of the header banners has a match with the current banner
    if (containsMatchedSpecificBanner && thisBannerContainsCurrentPage) {
        return true;
    }

    // this page is not registered in any banners and the current banner is a general banner
    if (!containsMatchedSpecificBanner && thisBannerIsGeneral) {
        return true;
    }

    // otherwise false
    return false;
};

const HeaderAlertBanner: React.FC<Props> = ({ fields, currentPageId, bannersInCurrentPage }) => {
    const [visible, setVisible] = React.useState(true);
    const [shouldRenderIt, setShouldRenderIt] = React.useState(false);

    React.useEffect(() => {
        let selectedPages = fields.links;
        if (!fields.selectPages) {
            selectedPages = [];
        }
        setShouldRenderIt(shouldRenderThisBanner(bannersInCurrentPage, selectedPages, currentPageId));
    }, [currentPageId]);

    const handleCloseBanner = () => {
        setVisible(false);
    };

    if (!shouldRenderIt || !visible) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.bannerContent}>
                {parse(fields.message, parserOptions)}
                <Link onClick={handleCloseBanner} {...styles.close} aria-label={translate("Close Banner")} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const headerAlertBanner: WidgetModule = {
    component: connect(mapStateToProps)(HeaderAlertBanner),
    definition: {
        group: "Header",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.message,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.selectPages,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                dependentFields: [fields.links],
            },
            {
                name: fields.links,
                displayName: "Selected Pages",
                editorTemplate: "ListField",
                isVisible: (item: HasFields) => item.fields.selectPages,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    return value;
                },
                validate: (links: HasFields[]) => {
                    if (!links || links.length === 0) {
                        return "At least one page must be added.";
                    }

                    return null;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                ],
            },
        ],
    },
};

export default headerAlertBanner;
